import styled, { css } from 'styled-components';

import { designTokens as t } from '../ui/foundation/DesignTokens';
import { CAPACITY_STATUS } from '../../utils/conversationLimit/capacity';
import { CLIENT_PANEL_VISIBLE_MEDIA } from '../../utils/conversationLimit/releasePlacement';

/**
 * Conversation limit ("modo cola") — styles. Tokens from the iVentas Design
 * System v4.0 (docs/design/dash-new-ui-B.html): pill controls, bordered cards,
 * Plus Jakarta Sans for numbers/buttons, Inter for data. Mobile-first.
 */

const STATUS_TONES = {
  [CAPACITY_STATUS.AVAILABLE]: { fg: t.colors.primary[600], bg: t.colors.primary[50], border: t.colors.primary[200] },
  [CAPACITY_STATUS.NEAR_LIMIT]: { fg: t.colors.warning[700], bg: t.colors.warning[50], border: t.colors.warning[200] },
  [CAPACITY_STATUS.SATURATED]: { fg: t.colors.error[700], bg: t.colors.error[50], border: t.colors.error[200] },
  [CAPACITY_STATUS.EXCEEDED]: { fg: '#FFFFFF', bg: t.colors.error[600], border: t.colors.error[600] },
};

export const toneOf = (status) => STATUS_TONES[status] || STATUS_TONES[CAPACITY_STATUS.AVAILABLE];

const pill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 4px 12px;
  border-radius: ${t.radius.full};
  font-family: ${t.fonts.heading};
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
`;

export const IndicatorBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 0;
`;

export const StatusPill = styled.span`
  ${pill};
  color: ${({ status }) => toneOf(status).fg};
  background: ${({ status }) => toneOf(status).bg};
  border: 1px solid ${({ status }) => toneOf(status).border};
`;

export const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: ${t.radius.full};
  background: currentColor;
`;

export const PillButton = styled.button`
  ${pill};
  cursor: pointer;
  color: ${t.colors.gray[700]};
  background: #ffffff;
  border: 1px solid ${t.colors.gray[200]};

  &:hover {
    border-color: ${t.colors.gray[300]};
    background: ${t.colors.gray[50]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${t.colors.primary.ring};
  }
`;

export const CountBadge = styled.span`
  min-width: 20px;
  padding: 0 6px;
  border-radius: ${t.radius.full};
  background: ${({ alert }) => (alert ? t.colors.accent[400] : t.colors.gray[100])};
  color: ${({ alert }) => (alert ? t.colors.accent.text : t.colors.gray[700])};
  font-size: 0.72rem;
  text-align: center;
`;

const dangerOutline = css`
  ${pill};
  justify-content: center;
  cursor: pointer;
  color: ${t.colors.error[600]};
  background: transparent;
  border: 1px solid ${t.colors.error[300]};

  &:hover:not(:disabled) {
    background: ${t.colors.error[50]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${t.colors.primary.ring};
  }
`;

/** Client-data panel placement: full-width row. */
export const DangerOutlineButton = styled.button`
  ${dangerOutline};
  width: 100%;
  min-height: 40px;
  margin: 8px 0;
`;

/**
 * Chat header placement: compact pill next to "Finalizar". Shown only while
 * the client-data panel (where the full-width button lives) is hidden.
 */
export const HeaderDangerButton = styled.button`
  ${dangerOutline};
  flex-shrink: 0;
  min-height: 34px;
  padding: 6px 12px;

  ${CLIENT_PANEL_VISIBLE_MEDIA} {
    display: none;
  }
`;

export const FieldError = styled.span`
  display: block;
  margin-top: 4px;
  color: ${t.colors.error[600]};
  font-family: ${t.fonts.body};
  font-size: 0.75rem;
`;

export const Hint = styled.span`
  display: block;
  margin-top: 4px;
  color: ${t.colors.gray[500]};
  font-family: ${t.fonts.body};
  font-size: 0.75rem;
`;

export const BodyText = styled.p`
  margin: 0;
  color: ${t.colors.gray[700]};
  font-family: ${t.fonts.body};
  font-size: 0.9rem;
  line-height: 1.5;
`;

/**
 * Where the «Carga por agente» body renders: the chat's modal (default, its
 * look unchanged) or Inicio's «Configuración» tab (v2, S14), where each part
 * is a card of its own on Inicio's px type scale.
 */
export const PANEL_VARIANTS = Object.freeze({ MODAL: 'modal', INICIO: 'inicio' });

/** The Inicio v2 card: 1px border, 18px radius, a faint shadow. */
export const inicioCard = css`
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid ${t.colors.gray[200]};
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  min-width: 0;
`;

/** «Carga por agente» as a card of Inicio: its title and the live note over the table. */
export const LoadCard = styled.section`
  ${inicioCard};
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;

export const LoadCardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

export const LoadCardTitle = styled.h2`
  margin: 0;
  color: ${t.colors.gray[900]};
  font-family: ${t.fonts.body};
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
`;

export const LiveNote = styled.span`
  color: ${t.colors.gray[600]};
  font-family: ${t.fonts.body};
  font-size: 12px;
  line-height: 16px;
`;
